import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Drawer,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  Button,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  CalendarMonth as CalendarMonthIcon,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
import type { CommissionStatus } from '../types/commission';

export interface CalendarCommission {
  propertyId: string;
  propertyAddress: string;
  soldPrice: number;
  commission: number;
  shareAmount: number;
  soldDate: string;
  settlementDate: string;
  status: CommissionStatus;
  agentNames?: string[];
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const statusConfig: Record<
  CommissionStatus,
  { color: string; label: string; icon: React.ReactNode }
> = {
  pending: { color: '#ed6c02', label: 'Pending', icon: <ScheduleIcon sx={{ fontSize: 12 }} /> },
  paid: { color: '#2e7d32', label: 'Paid', icon: <CheckCircleIcon sx={{ fontSize: 12 }} /> },
  cancelled: { color: '#9e9e9e', label: 'Cancelled', icon: <CancelIcon sx={{ fontSize: 12 }} /> },
};

function toDateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface CommissionCalendarProps {
  properties: CalendarCommission[];
  agencySlug: string;
  isAdmin: boolean;
  onStatusChange: (propertyId: string, status: CommissionStatus) => Promise<void>;
  statusUpdatingPropertyId: string | null;
  /** When set, drawer shows checkboxes and "Mark selected as paid" for bulk update (cancel is per-property only) */
  onBulkStatusChange?: (propertyIds: string[], status: CommissionStatus) => Promise<void>;
  bulkUpdating?: boolean;
}

export default function CommissionCalendar({
  properties,
  agencySlug,
  isAdmin,
  onStatusChange,
  statusUpdatingPropertyId,
  onBulkStatusChange,
  bulkUpdating = false,
}: CommissionCalendarProps) {
  const [monthRef, setMonthRef] = React.useState(() => {
    const n = new Date();
    return new Date(n.getFullYear(), n.getMonth(), 1);
  });
  const [selectedDayKey, setSelectedDayKey] = React.useState<string | null>(null);
  const [selectedDrawerPropertyIds, setSelectedDrawerPropertyIds] = React.useState<Set<string>>(new Set());

  const year = monthRef.getFullYear();
  const month = monthRef.getMonth();

  const byDay = useMemo(() => {
    const map: Record<string, CalendarCommission[]> = {};
    for (const p of properties) {
      const dateStr = p.settlementDate || p.soldDate;
      const d = dateStr ? new Date(dateStr) : null;
      if (!d || isNaN(d.getTime())) continue;
      const key = toDateKey(d);
      if (d.getFullYear() !== year || d.getMonth() !== month) continue;
      if (!map[key]) map[key] = [];
      map[key].push(p);
    }
    return map;
  }, [properties, year, month]);

  const { startPad, totalDays } = useMemo(() => {
    const first = new Date(year, month, 1);
    const last = new Date(year, month + 1, 0);
    return { startPad: first.getDay(), totalDays: last.getDate() };
  }, [year, month]);

  const prevMonth = () => setMonthRef((m) => new Date(m.getFullYear(), m.getMonth() - 1, 1));
  const nextMonth = () => setMonthRef((m) => new Date(m.getFullYear(), m.getMonth() + 1, 1));

  const dayCells = useMemo(() => {
    const cells: { key: string; day: number; isCurrentMonth: boolean; commissions: CalendarCommission[] }[] = [];
    for (let i = 0; i < startPad; i++) {
      const prev = new Date(year, month, -startPad + i + 1);
      cells.push({ key: `pad-${i}`, day: prev.getDate(), isCurrentMonth: false, commissions: [] });
    }
    for (let d = 1; d <= totalDays; d++) {
      const key = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ key, day: d, isCurrentMonth: true, commissions: byDay[key] ?? [] });
    }
    const remainder = (7 - ((startPad + totalDays) % 7)) % 7;
    for (let r = 0; r < remainder; r++) {
      cells.push({ key: `pad-end-${r}`, day: r + 1, isCurrentMonth: false, commissions: [] });
    }
    return cells;
  }, [year, month, startPad, totalDays, byDay]);

  const selectedDayCommissions = useMemo(() => {
    if (!selectedDayKey) return [];
    return byDay[selectedDayKey] ?? [];
  }, [selectedDayKey, byDay]);

  const pendingCount = useMemo(
    () => Object.values(byDay).flat().filter((p) => (p.status ?? 'pending') === 'pending').length,
    [byDay]
  );

  return (
    <Card>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarMonthIcon color="action" />
            <Typography variant="h6">Commission calendar</Typography>
            {pendingCount > 0 && (
              <Chip
                size="small"
                label={`${pendingCount} pending`}
                color="warning"
                variant="outlined"
              />
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <IconButton size="small" onClick={prevMonth} aria-label="Previous month">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight="600" sx={{ minWidth: 160, textAlign: 'center' }}>
              {MONTHS[month]} {year}
            </Typography>
            <IconButton size="small" onClick={nextMonth} aria-label="Next month">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Click a day to view commissions. {isAdmin && 'Admins can mark status (e.g. pending → paid) in the day detail.'}
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ overflow: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                {WEEKDAYS.map((w) => (
                  <TableCell key={w} align="center" sx={{ fontWeight: 600, py: 1 }}>
                    {w}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: Math.ceil(dayCells.length / 7) }, (_, rowIndex) => (
                <TableRow key={rowIndex}>
                  {dayCells.slice(rowIndex * 7, rowIndex * 7 + 7).map((cell) => {
                    const isSelected = selectedDayKey === cell.key && cell.isCurrentMonth;
                    return (
                      <TableCell
                        key={cell.key}
                        align="center"
                        sx={{
                          py: 1,
                          verticalAlign: 'top',
                          cursor: cell.isCurrentMonth ? 'pointer' : 'default',
                          bgcolor: cell.isCurrentMonth
                            ? isSelected
                              ? 'action.selected'
                              : 'transparent'
                            : 'action.hover',
                          border: '1px solid',
                          borderColor: 'divider',
                          minWidth: 56,
                          maxWidth: 56,
                        }}
                        onClick={() => cell.isCurrentMonth && setSelectedDayKey(cell.key)}
                      >
                        {cell.isCurrentMonth ? (
                          <>
                            <Typography variant="body2" fontWeight={isSelected ? 700 : 500}>
                              {cell.day}
                            </Typography>
                            <Box
                              display="flex"
                              flexWrap="wrap"
                              justifyContent="center"
                              gap={0.25}
                              sx={{ mt: 0.25 }}
                            >
                              {cell.commissions.slice(0, 3).map((p) => {
                                const cfg = statusConfig[p.status] ?? statusConfig.pending;
                                return (
                                  <Box
                                    key={p.propertyId}
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      bgcolor: cfg.color,
                                    }}
                                    title={`${p.propertyAddress} · ${cfg.label}`}
                                  />
                                );
                              })}
                              {cell.commissions.length > 3 && (
                                <Typography variant="caption" color="text.secondary">
                                  +{cell.commissions.length - 3}
                                </Typography>
                              )}
                            </Box>
                          </>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            {cell.day}
                          </Typography>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box display="flex" flexWrap="wrap" gap={2} sx={{ mt: 2 }} justifyContent="center">
          {(Object.keys(statusConfig) as CommissionStatus[]).map((s) => {
            const cfg = statusConfig[s];
            return (
              <Box key={s} display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: cfg.color }} />
                <Typography variant="caption" color="text.secondary">
                  {cfg.label}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </CardContent>

      <Drawer
        anchor="right"
        open={!!selectedDayKey}
        onClose={() => {
          setSelectedDayKey(null);
          setSelectedDrawerPropertyIds(new Set());
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            maxHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box p={2} height="100%" display="flex" flexDirection="column" minHeight={0} overflow="hidden">
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexShrink={0}>
            <Typography variant="h6" component="h2">
              {selectedDayKey
                ? (() => {
                    const [y, m, d] = selectedDayKey.split('-').map(Number);
                    return new Date(y, m - 1, d).toLocaleDateString('en-AU', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    });
                  })()
                : ''}
            </Typography>
            <IconButton size="small" onClick={() => setSelectedDayKey(null)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </Box>

          {selectedDayCommissions.length === 0 ? (
            <Box py={3} textAlign="center">
              <Typography color="text.secondary" variant="body2">
                No commissions on this day.
              </Typography>
              <Typography color="text.secondary" variant="caption" display="block" sx={{ mt: 0.5 }}>
                Select another day or month to view commissions.
              </Typography>
            </Box>
          ) : (
            <>
              {isAdmin && onBulkStatusChange && selectedDrawerPropertyIds.size > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={2} flexShrink={0}>
                  <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {selectedDrawerPropertyIds.size} selected
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="success"
                    startIcon={<CheckCircleIcon />}
                    onClick={() => {
                      onBulkStatusChange(Array.from(selectedDrawerPropertyIds), 'paid');
                      setSelectedDrawerPropertyIds(new Set());
                    }}
                    disabled={bulkUpdating}
                  >
                    Mark as paid
                  </Button>
                </Box>
              )}
              <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {isAdmin && onBulkStatusChange && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            indeterminate={
                              selectedDrawerPropertyIds.size > 0 &&
                              selectedDrawerPropertyIds.size < selectedDayCommissions.length
                            }
                            checked={
                              selectedDayCommissions.length > 0 &&
                              selectedDrawerPropertyIds.size === selectedDayCommissions.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDrawerPropertyIds(
                                  new Set(selectedDayCommissions.map((p) => p.propertyId))
                                );
                              } else {
                                setSelectedDrawerPropertyIds(new Set());
                              }
                            }}
                            aria-label="Select all"
                          />
                        </TableCell>
                      )}
                      <TableCell>Property / Agent</TableCell>
                      <TableCell align="right">Commission</TableCell>
                      {isAdmin && <TableCell align="right">Status</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedDayCommissions.map((p) => {
                      const cfg = statusConfig[p.status] ?? statusConfig.pending;
                      const agents = (p.agentNames ?? []).join(', ') || '—';
                      return (
                        <TableRow key={p.propertyId}>
                          {isAdmin && onBulkStatusChange && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedDrawerPropertyIds.has(p.propertyId)}
                                onChange={(e) => {
                                  setSelectedDrawerPropertyIds((prev) => {
                                    const next = new Set(prev);
                                    if (e.target.checked) next.add(p.propertyId);
                                    else next.delete(p.propertyId);
                                    return next;
                                  });
                                }}
                                aria-label={`Select ${p.propertyAddress}`}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                          {agencySlug ? (
                            <Link
                              to={`/agencies/${agencySlug}/properties/${p.propertyId}`}
                              style={{ color: 'inherit', fontWeight: 500 }}
                            >
                              <Typography variant="body2" fontWeight="medium" component="span">
                                {p.propertyAddress}
                              </Typography>
                            </Link>
                          ) : (
                            <Typography variant="body2" fontWeight="medium">
                              {p.propertyAddress}
                            </Typography>
                          )}
                          <Typography variant="caption" color="text.secondary" display="block">
                            {agents}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            $
                            {p.commission.toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </Typography>
                        </TableCell>
                        {isAdmin && (
                          <TableCell align="right">
                            <FormControl size="small" sx={{ minWidth: 110 }} disabled={statusUpdatingPropertyId === p.propertyId}>
                              <Select
                                value={p.status ?? 'pending'}
                                onChange={(e) => onStatusChange(p.propertyId, e.target.value as CommissionStatus)}
                                displayEmpty
                              >
                                <MenuItem value="pending">
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <ScheduleIcon fontSize="small" />
                                    Pending
                                  </Box>
                                </MenuItem>
                                <MenuItem value="paid">
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CheckCircleIcon fontSize="small" />
                                    Paid
                                  </Box>
                                </MenuItem>
                                <MenuItem value="cancelled">
                                  <Box display="flex" alignItems="center" gap={0.5}>
                                    <CancelIcon fontSize="small" />
                                    Cancelled
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            </>
          )}
        </Box>
      </Drawer>
    </Card>
  );
}
